import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout/index';
import {
    alpha,
    Box,
    Button,
    FormLabel,
    IconButton,
    Stack,
    Switch,
    Theme,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import UploadImage from 'components/common/upload-control/UploadImage';

type NavigateType = 'external_link' | 'app_page';
type IconType = 'mi' | 'upload' | 'url';

interface SubmenuItem {
    i: number;
    title: string;
    navigate_to: NavigateType;
    link: string;
    app_page_id: string;
}

interface MenuItemData {
    i: number;
    title: string;
    icon_type: IconType;
    icon_value: string;
    icon_upload: string;
    navigate_to: NavigateType;
    link: string;
    app_page_id: string;
    show_submenu: boolean;
    items: SubmenuItem[];
}

export interface FormSubmenuListProps extends FormControlProps {
    appPagesUrl: string;
    source: string;
    ajaxApiUrl: string;
    ajaxApiRemoveUrl: string;
}

const UploadContext = createContext({ ajaxApiUrl: '', ajaxApiRemoveUrl: '' });

const createNewSubmenuItem = (index: number): SubmenuItem => ({
    i: index,
    title: '',
    navigate_to: 'external_link',
    link: '',
    app_page_id: '',
});

const createDefaultMenuItemData = (index: number): MenuItemData => ({
    i: index,
    title: '',
    icon_type: 'mi',
    icon_value: '',
    icon_upload: '',
    navigate_to: 'external_link',
    link: '',
    app_page_id: '',
    show_submenu: false,
    items: [],
});

const serializeMenuItems = (menuItems: MenuItemData[]) => {
    return JSON.stringify(
        menuItems.map(({ i, items, ...rest }) => ({
            ...rest,
            items: items.map(({ i: _itemI, ...itemRest }) => itemRest),
        })),
    );
};

export default function FormSubmenuList({ controlProps }: FormElementControlPropsType<FormSubmenuListProps>) {
    const { t } = useBundleTranslation();
    const { ajaxApiUrl, ajaxApiRemoveUrl } = controlProps;
    const nextIndex = useRef(0);
    const nextSubmenuIndex = useRef(0);
    const [menuItems, setMenuItems] = useState<MenuItemData[]>([]);

    // Fetch app pages
    const { data: appPages } = useQuery<FormComponentValue[], Error>({
        queryKey: ['FormSubmenuList', 'app_pages', controlProps.appPagesUrl],
        queryFn: async () => {
            const response = await instance.get<APIResponse<FormComponentValue[]>>(controlProps.appPagesUrl, {
                params: { source: controlProps.source },
            });
            return response.data.status === 'OK' ? response.data.data : [];
        },
    });
    const appPagesOptions = useMemo(() => [{ value: '', label: '--' }, ...(appPages ?? [])], [appPages]);

    // Initialize component value
    useEffect(() => {
        let defaultValue: any = controlProps.value;
        if (typeof defaultValue === 'string') {
            try {
                defaultValue = JSON.parse(defaultValue);
            } catch (e) {
                defaultValue = null;
            }
        }

        if (Array.isArray(defaultValue)) {
            // New format: array of menu items
            const items = defaultValue.map((item: any) => ({
                ...createDefaultMenuItemData(nextIndex.current++),
                ...item,
                items: (item.items ?? []).map((sub: any) => ({
                    ...sub,
                    i: nextSubmenuIndex.current++,
                })),
            }));
            setMenuItems(items.length > 0 ? items : [createDefaultMenuItemData(nextIndex.current++)]);
        } else if (defaultValue && typeof defaultValue === 'object') {
            // Backward compat: single object
            const items = (defaultValue.items ?? []).map((sub: any) => ({
                ...sub,
                i: nextSubmenuIndex.current++,
            }));
            setMenuItems([
                {
                    ...createDefaultMenuItemData(nextIndex.current++),
                    ...defaultValue,
                    items,
                },
            ]);
        } else {
            setMenuItems([createDefaultMenuItemData(nextIndex.current++)]);
        }
    }, []);

    const updateValue = useCallback(
        (newItems: MenuItemData[]) => {
            setMenuItems(newItems);
            controlProps.onChange(serializeMenuItems(newItems));
        },
        [controlProps],
    );

    const handleMenuItemChange = useCallback(
        (menuIndex: number, newData: MenuItemData) => {
            setMenuItems((prev) => {
                // Auto-add first submenu item when enabling submenu
                if (newData.show_submenu && !prev[menuIndex].show_submenu && newData.items.length === 0) {
                    newData = { ...newData, items: [createNewSubmenuItem(nextSubmenuIndex.current++)] };
                }
                const updated = [...prev];
                updated[menuIndex] = newData;
                controlProps.onChange(serializeMenuItems(updated));
                return updated;
            });
        },
        [controlProps],
    );

    const handleSubmenuRowChange = useCallback(
        (menuIndex: number, subIndex: number, row: SubmenuItem) => {
            setMenuItems((prev) => {
                const updated = [...prev];
                const newItems = [...updated[menuIndex].items];
                newItems[subIndex] = { ...newItems[subIndex], ...row };
                updated[menuIndex] = { ...updated[menuIndex], items: newItems };
                controlProps.onChange(serializeMenuItems(updated));
                return updated;
            });
        },
        [controlProps],
    );

    const handleSubmenuDelete = useCallback(
        (menuIndex: number, rowI: number) => {
            setMenuItems((prev) => {
                const updated = [...prev];
                const subIndex = updated[menuIndex].items.findIndex((item) => item.i === rowI);
                if (subIndex === -1) return prev;
                const newItems = [...updated[menuIndex].items];
                newItems.splice(subIndex, 1);
                updated[menuIndex] = { ...updated[menuIndex], items: newItems };
                controlProps.onChange(serializeMenuItems(updated));
                return updated;
            });
        },
        [controlProps],
    );

    const handleAddSubmenuItem = useCallback(
        (menuIndex: number) => {
            setMenuItems((prev) => {
                const updated = [...prev];
                const newRow = createNewSubmenuItem(nextSubmenuIndex.current++);
                updated[menuIndex] = { ...updated[menuIndex], items: [...updated[menuIndex].items, newRow] };
                controlProps.onChange(serializeMenuItems(updated));
                return updated;
            });
        },
        [controlProps],
    );

    const handleAddMenuItem = () => {
        const newItem = createDefaultMenuItemData(nextIndex.current++);
        updateValue([...menuItems, newItem]);
    };

    const handleDeleteMenuItem = (menuIndex: number) => {
        const updated = menuItems.filter((_, idx) => idx !== menuIndex);
        updateValue(updated);
    };

    return (
        <UploadContext.Provider value={{ ajaxApiUrl, ajaxApiRemoveUrl }}>
            <Box>
                <Stack direction="column" spacing={1}>
                    {menuItems.map((menuItem, menuIndex) => (
                        <MenuItemBlock
                            key={menuItem.i}
                            menuItem={menuItem}
                            menuIndex={menuIndex}
                            canDelete={menuItems.length > 1}
                            appPagesOptions={appPagesOptions}
                            onMenuItemChange={handleMenuItemChange}
                            onSubmenuRowChange={handleSubmenuRowChange}
                            onSubmenuDelete={handleSubmenuDelete}
                            onAddSubmenuItem={handleAddSubmenuItem}
                            onDeleteMenuItem={handleDeleteMenuItem}
                            t={t}
                        />
                    ))}
                    <Box>
                        <Button onClick={handleAddMenuItem} startIcon={<IconMi icon="new" />} variant="light">
                            {t('add_item')}
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </UploadContext.Provider>
    );
}

interface MenuItemBlockProps {
    menuItem: MenuItemData;
    menuIndex: number;
    canDelete: boolean;
    appPagesOptions: FormComponentValue[];
    onMenuItemChange: (menuIndex: number, data: MenuItemData) => void;
    onSubmenuRowChange: (menuIndex: number, subIndex: number, row: SubmenuItem) => void;
    onSubmenuDelete: (menuIndex: number, rowI: number) => void;
    onAddSubmenuItem: (menuIndex: number) => void;
    onDeleteMenuItem: (menuIndex: number) => void;
    t: (key: string) => string;
}

function MenuItemBlock({
    menuItem,
    menuIndex,
    canDelete,
    appPagesOptions,
    onMenuItemChange,
    onSubmenuRowChange,
    onSubmenuDelete,
    onAddSubmenuItem,
    onDeleteMenuItem,
    t,
}: MenuItemBlockProps) {
    return (
        <Box>
            <Stack direction="column" spacing={0}>
                <MainMenuRow
                    row={menuItem}
                    appPagesOptions={appPagesOptions}
                    onChange={(newData) => onMenuItemChange(menuIndex, newData)}
                    canDelete={canDelete}
                    onDelete={() => onDeleteMenuItem(menuIndex)}
                    t={t}
                />

                {menuItem.show_submenu && (
                    <>
                        {menuItem.items.map((row, subIndex) => (
                            <SubmenuRow
                                key={row.i}
                                row={row}
                                index={subIndex}
                                isFirst={subIndex === 0}
                                isLast={subIndex === menuItem.items.length - 1}
                                appPagesOptions={appPagesOptions}
                                onChange={(newRow) => onSubmenuRowChange(menuIndex, subIndex, newRow)}
                                onDelete={() => onSubmenuDelete(menuIndex, row.i)}
                                t={t}
                            />
                        ))}
                        <Box
                            sx={{
                                border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                borderTop: 'none',
                            }}
                        >
                            <Button
                                onClick={() => onAddSubmenuItem(menuIndex)}
                                startIcon={<IconMi icon="new" />}
                                variant="text"
                            >
                                {t('submenu_item')}
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>
        </Box>
    );
}

interface MainMenuRowProps {
    row: MenuItemData;
    appPagesOptions: FormComponentValue[];
    onChange: (row: MenuItemData) => void;
    canDelete: boolean;
    onDelete: () => void;
    t: (key: string) => string;
}

function MainMenuRow({ row, appPagesOptions, onChange, canDelete, onDelete, t }: MainMenuRowProps) {
    const form = useCustomSimplifiedForm(row);
    const watch = form.form.hookFormWatch();

    // Sync only form-managed fields to parent; items are managed by parent (add/delete)
    useEffect(() => {
        const { items: _w, ...watchFields } = watch;
        const { items: _r, ...rowFields } = row;
        if (JSON.stringify(watchFields) !== JSON.stringify(rowFields)) {
            onChange({ ...watchFields, items: row.items } as MenuItemData);
        }
    }, [watch, row, onChange]);

    const handleIconTypeChange = (_: React.MouseEvent<HTMLElement>, newValue: IconType | null) => {
        if (newValue !== null) {
            form.form.hookFormSetValue('icon_type', newValue);
            // Clear icon value when switching types
            form.form.hookFormSetValue('icon_value', '');
        }
    };

    const handleNavigateTypeChange = (_: React.MouseEvent<HTMLElement>, newValue: NavigateType | null) => {
        if (newValue !== null) {
            form.form.hookFormSetValue('navigate_to', newValue);
        }
    };

    const handleToggleSubmenu = () => {
        form.form.hookFormSetValue('show_submenu', !watch.show_submenu);
    };

    return (
        <Stack
            direction="column"
            spacing={1}
            sx={{
                border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                p: 1,
                position: 'relative',
                '.select-holder': {
                    overflow: 'visible',
                },
            }}
        >
            {canDelete && (
                <IconButton sx={{ position: 'absolute', top: 4, right: 4, padding: 0 }} onClick={onDelete}>
                    <IconMi icon="times" fontSize="16" />
                </IconButton>
            )}
            <Box>
                <TemplateFormComponent
                    elementProps={form}
                    component="FormText"
                    name="title"
                    designVariant="secondary"
                />
            </Box>

            {/* Icon */}
            <Box>
                <FormLabel className="design-variant--secondary">{t('icon')}</FormLabel>
                <Box sx={{ mt: 0.5 }}>
                    <ToggleButtonGroup
                        color="primary"
                        value={watch.icon_type}
                        exclusive
                        onChange={handleIconTypeChange}
                        fullWidth
                        className="design-variant--secondary"
                        sx={{ mb: 1 }}
                    >
                        <ToggleButton value="mi">{t('mi_icon')}</ToggleButton>
                        <ToggleButton value="upload">{t('upload_image')}</ToggleButton>
                        <ToggleButton value="url">{t('url')}</ToggleButton>
                    </ToggleButtonGroup>

                    {watch.icon_type === 'mi' && (
                        <TemplateFormComponent
                            elementProps={form}
                            component="ChooseIconSvg"
                            name="icon_value"
                            designVariant="secondary"
                        />
                    )}

                    {watch.icon_type === 'upload' && (
                        <UploadImageToPath
                            value={watch.icon_upload}
                            onChange={(path) => form.form.hookFormSetValue('icon_upload', path)}
                        />
                    )}

                    {watch.icon_type === 'url' && (
                        <TemplateFormComponent
                            elementProps={form}
                            component="FormText"
                            name="icon_value"
                            props={{ placeholder: '' }}
                            designVariant="secondary"
                        />
                    )}
                </Box>
            </Box>

            {!watch.show_submenu && (
                <>
                    <Box>
                        <FormLabel className="design-variant--secondary">{t('navigate_to')}</FormLabel>
                        <ToggleButtonGroup
                            color="primary"
                            value={watch.navigate_to}
                            exclusive
                            onChange={handleNavigateTypeChange}
                            fullWidth
                            className="design-variant--secondary"
                        >
                            <ToggleButton value="external_link">{t('external_link')}</ToggleButton>
                            <ToggleButton value="app_page">{t('app_page')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {watch.navigate_to === 'external_link' && (
                        <Box>
                            <TemplateFormComponent
                                elementProps={form}
                                component="FormText"
                                name="link"
                                props={{ placeholder: '' }}
                                designVariant="secondary"
                            />
                        </Box>
                    )}

                    {watch.navigate_to === 'app_page' && (
                        <Box>
                            <FormLabel className="design-variant--secondary">{t('app_page')}</FormLabel>
                            <TemplateFormComponent
                                componentValues={appPagesOptions}
                                component="FormSelect"
                                elementProps={form}
                                name="app_page_id"
                                designVariant="secondary"
                            />
                        </Box>
                    )}
                </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch checked={watch.show_submenu} onChange={handleToggleSubmenu} color="primary" />
                <FormLabel sx={{ ml: 1, mb: 0, cursor: 'pointer' }} onClick={handleToggleSubmenu}>
                    {t('show_submenu')}
                </FormLabel>
            </Box>
        </Stack>
    );
}

interface SubmenuRowProps {
    row: SubmenuItem;
    index: number;
    isFirst: boolean;
    isLast: boolean;
    appPagesOptions: FormComponentValue[];
    onChange: (row: SubmenuItem) => void;
    onDelete: () => void;
    t: (key: string) => string;
}

function SubmenuRow({ row, index, isFirst, isLast, appPagesOptions, onChange, onDelete, t }: SubmenuRowProps) {
    const form = useCustomSimplifiedForm(row);
    const watch = form.form.hookFormWatch();

    useEffect(() => {
        if (JSON.stringify(watch) !== JSON.stringify(row)) {
            onChange(watch);
        }
    }, [watch, row, onChange]);

    const handleNavigateTypeChange = (_: React.MouseEvent<HTMLElement>, newValue: NavigateType | null) => {
        if (newValue !== null) {
            form.form.hookFormSetValue('navigate_to', newValue);
        }
    };

    return (
        <Stack
            direction="row"
            alignContent="center"
            sx={{
                border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                borderTop: 'none',
                ...(isLast && { borderBottom: 'none' }),
                marginTop: '0 !important',
            }}
        >
            <Stack
                direction="column"
                spacing={1}
                width="100%"
                sx={{
                    p: 1,
                    '.select-holder': {
                        overflow: 'visible',
                    },
                }}
            >
                <TemplateFormComponent
                    elementProps={form}
                    component="FormText"
                    label={`${t('submenu_item')} ${index + 1}`}
                    name="title"
                    props={{ autoFocus: true }}
                    designVariant="secondary"
                />

                <Box>
                    <FormLabel className="design-variant--secondary">{t('navigate_to')}</FormLabel>
                    <ToggleButtonGroup
                        color="primary"
                        value={watch.navigate_to}
                        exclusive
                        onChange={handleNavigateTypeChange}
                        fullWidth
                        className="design-variant--secondary"
                    >
                        <ToggleButton value="external_link">{t('external_link')}</ToggleButton>
                        <ToggleButton value="app_page">{t('app_page')}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {watch.navigate_to === 'external_link' && (
                    <Box>
                        <TemplateFormComponent
                            elementProps={form}
                            component="FormText"
                            name="link"
                            designVariant="secondary"
                        />
                    </Box>
                )}

                {watch.navigate_to === 'app_page' && (
                    <Box>
                        <FormLabel className="design-variant--secondary">{t('app_page')}</FormLabel>
                        <TemplateFormComponent
                            componentValues={appPagesOptions}
                            component="FormSelect"
                            elementProps={form}
                            name="app_page_id"
                            designVariant="secondary"
                        />
                    </Box>
                )}
            </Stack>

            {!isFirst && (
                <Box sx={{ p: 1, pl: 0 }}>
                    <IconButton sx={{ padding: 0 }} onClick={onDelete}>
                        <IconMi icon="times" fontSize="16" />
                    </IconButton>
                </Box>
            )}
        </Stack>
    );
}

interface UploadImageToPathProps {
    value: string;
    onChange: (path: string) => void;
}

function UploadImageToPath({ value, onChange }: UploadImageToPathProps) {
    const { ajaxApiUrl, ajaxApiRemoveUrl } = useContext(UploadContext);
    const subForm = useCustomSimplifiedForm({
        icon_upload: value || '',
        icon_upload_state: '',
    });
    const watch = subForm.form.hookFormWatch();

    useEffect(() => {
        const iconUpload = watch.icon_upload;
        if (typeof iconUpload === 'string' && iconUpload !== value) {
            onChange(iconUpload);
        } else if (Array.isArray(iconUpload) && iconUpload.length === 0 && value !== '') {
            onChange('');
        }
    }, [watch.icon_upload]);

    return (
        <UploadImage
            controlProps={{
                form: subForm.form,
                name: 'icon_upload',
                onChange: (eventOrValue: any) => {
                    const files = eventOrValue?.target?.files;
                    subForm.form.hookFormSetValue('icon_upload', files !== undefined ? files : eventOrValue);
                },
                value: watch.icon_upload,
                removeActionText: '',
                removeActionView: 'button',
                removeButtonColor: 'neutral',
                accept: 'image/*',
                previewsList: value ? [value] : [],
                showRemoveAction: true,
                ajaxApiUrl,
                ajaxApiRemoveUrl,
                onAjaxUploadResult: (response: any) => {
                    if (response.data.status === 'OK') {
                        subForm.form.hookFormSetValue('icon_upload', response.data.relative_path);
                    }
                },
            }}
        />
    );
}

export class FormSubmenuListBuilder extends FormComponentBuilder {
    prepareProps(): FormSubmenuListProps {
        return {
            ...this.controlProps,
            appPagesUrl: this.elementProps.componentProps?.appPagesUrl ?? '/data/app/select-data',
            source: this.elementProps.componentProps?.source,
            ajaxApiUrl: this.elementProps.componentProps?.ajaxApiUrl ?? '',
            ajaxApiRemoveUrl: this.elementProps.componentProps?.ajaxApiRemoveUrl ?? '',
        };
    }
}
